import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Progress } from '../ui/progress';
import { 
  RotateCcw, Maximize2, Minimize2, Cloud, CloudOff, X
} from 'lucide-react';
import { 
  CameraLensIcon, StarburstIcon, GlyphIcon, LightbulbAlchemyIcon,
  CheckAlchemyIcon, CrossAlchemyIcon, CrystalIcon, SpiralIcon
} from '../icons/AlchemyIcons';
import {
  FeelIcon, ObserveIcon, SaveIcon, InsightIcon, CheckIcon,
  ArrowRightIcon, ArrowLeftIcon, ChapterIcon
} from '../icons/ElchmeIcons';
import { toast } from 'sonner';
import AIPracticeScenario from '../shared/AIPracticeScenario';
import FloatingFeelingsHelper from '../shared/FloatingFeelingsHelper';

const CHAPTER_IMAGES = {
  hero: 'https://images.unsplash.com/photo-1516534775068-ba3e7458af70?w=1200&q=80', // Observer nature
  camera: 'https://images.unsplash.com/photo-1516035069371-29a1b244cc32?w=800&q=80', // Camera lens
  confusion: 'https://images.unsplash.com/photo-1623356301535-95f15498a634?w=800&q=80', // Confusion/Fog
  clarity: 'https://images.unsplash.com/photo-1470252649378-9c29740c9fa8?w=800&q=80', // Clear morning
};

export default function Chapter2Presentation() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section')) || 0;
  const [currentSection, setCurrentSection] = useState(initialSection);
  const [isPresentationMode, setIsPresentationMode] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  
  // Exercise states
  const [observationExercises, setObservationExercises] = useState({});
  const [showExerciseAnswers, setShowExerciseAnswers] = useState({});
  
  // Quiz Game states
  const [quizScore, setQuizScore] = useState(0);
  const [quizAnswered, setQuizAnswered] = useState({});
  const [quizComplete, setQuizComplete] = useState(false);
  const [currentQuizIndex, setCurrentQuizIndex] = useState(0);

  const exerciseData = [
    {
      id: 1,
      statement: "When you got really mad at me...",
      isObservation: false,
      rewrite: "When you raised your voice and hit the table...",
      hint: "Can a camera see 'mad'? What behaviors did you see/hear?"
    },
    {
      id: 2,
      statement: "When your voice was louder than I usually hear it...",
      isObservation: true,
      rewrite: null,
      hint: "This describes specific, measurable volume levels."
    },
    {
      id: 3,
      statement: "You were being so aggressive this morning.",
      isObservation: false,
      rewrite: "When you stood in the doorway and spoke while I was trying to leave...",
      hint: "'Aggressive' is an evaluation. What did they actually do?"
    },
    {
      id: 4,
      statement: "You ignored me when I walked in.",
      isObservation: false,
      rewrite: "When I walked in and said hello, you continued looking at your phone...",
      hint: "'Ignored' implies intent. What observable action happened?"
    },
    {
      id: 5,
      statement: "I saw you check your watch three times in the last 5 minutes.",
      isObservation: true,
      rewrite: null,
      hint: "This is a factual count of behavior."
    }
  ];

  // Load progress
  useEffect(() => {
    const token = localStorage.getItem('token');
    setIsLoggedIn(!!token);
    
    // Load local
    const saved = localStorage.getItem('chapter2Responses');
    if (saved) {
      setObservationExercises(JSON.parse(saved));
    }

    // Load server if logged in
    if (token) {
      fetchUserProgress(token);
    }
  }, []);

  const fetchUserProgress = async (token) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter2`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        if (data.responses) {
          setObservationExercises(data.responses);
          localStorage.setItem('chapter2Responses', JSON.stringify(data.responses));
          setLastSyncTime(new Date());
        }
      }
    } catch (error) {
      console.log('Could not fetch progress from server');
    }
  };

  const saveProgress = async () => {
    localStorage.setItem('chapter2Responses', JSON.stringify(observationExercises));
    
    if (isLoggedIn) {
      setIsSyncing(true);
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/user/workbook-progress/chapter2`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ responses: observationExercises })
        });
        if (response.ok) {
          setLastSyncTime(new Date());
          toast.success('Progress saved to your account!');
        }
      } catch (error) {
        toast.error('Saved locally (server sync failed)');
      } finally {
        setIsSyncing(false);
      }
    } else {
      toast.success('Saved locally! Sign in to sync across devices.');
    }
  };

  // Presentation sections
  // Full quiz data from workbook page 97 answers
  const quizData = [
    { id: 1, statement: "When you manipulated me yesterday...", isObservation: false, rewrite: "When you asked me to do something after I had said no...", explanation: "'Manipulated' is a judgment/evaluation of intent, not observable behavior." },
    { id: 2, statement: "You are not a very good listener.", isObservation: false, rewrite: "When I was talking, you picked up your phone and started reading...", explanation: "'Not a good listener' is a judgment. What specific behavior led to this conclusion?" },
    { id: 3, statement: "My dad abandoned me and now I can't even think about him without becoming so angry.", isObservation: false, rewrite: "When my dad left our home when I was 8 and didn't call...", explanation: "'Abandoned' contains emotional evaluation. Describe what happened observably." },
    { id: 4, statement: "When I heard her say, 'John is so boring,' I felt...", isObservation: true, rewrite: null, explanation: "This IS an observation - it quotes exact words heard. The speaker observed specific words." },
    { id: 5, statement: "When my partner tries to control me, I feel like he has a lot of anger issues.", isObservation: false, rewrite: "When my partner asked where I was going and who I'd be with...", explanation: "'Control' and 'anger issues' are both evaluations. What did they actually say or do?" },
    { id: 6, statement: "You're depressed.", isObservation: false, rewrite: "I notice you've stayed in bed past noon three days this week...", explanation: "Diagnosis! A camera can't see 'depressed' - describe observable behaviors." },
    { id: 7, statement: "When I noticed that the garbage was still inside after I asked you to take it out, I felt...", isObservation: true, rewrite: null, explanation: "Pure observation! This describes a specific, verifiable situation without judgment." },
    { id: 8, statement: "When I see that you don't try as hard as you can, I feel...", isObservation: false, rewrite: "When I noticed you submitted the assignment without reviewing it...", explanation: "'Don't try as hard as you can' assumes knowledge of someone's capacity - evaluation!" },
    { id: 9, statement: "You don't seem to be having a good time.", isObservation: false, rewrite: "I notice you've been looking at your phone and haven't smiled in an hour...", explanation: "'Seem' language often signals interpretation. Describe observable signs." },
    { id: 10, statement: "You are so beautiful.", isObservation: false, rewrite: "When I look at your face, especially your eyes, I feel...", explanation: "Even positive evaluations aren't observations! What specifically do you see?" },
    { id: 11, statement: "My girlfriend doesn't care about me.", isObservation: false, rewrite: "When my girlfriend didn't call on my birthday...", explanation: "'Doesn't care' is mind-reading. What actions led to this interpretation?" },
    { id: 12, statement: "That was so sweet of you to say that.", isObservation: false, rewrite: "When you said 'I appreciate everything you do,' I felt...", explanation: "'Sweet' is an evaluation. What exactly did they say that affected you?" },
  ];

  const sections = [
    { id: 'intro', title: 'What is an Observation?', page: 94 },
    { id: 'why', title: 'Why say the Observation?', page: 94 },
    { id: 'remember', title: 'Things to Remember', page: 94 },
    { id: 'hard', title: 'Why is it so Hard?', page: 94 },
    { id: 'pattern', title: 'The Pattern of Suffering', page: 94 },
    { id: 'exercise-intro', title: 'Practice: Observation or Evaluation?', page: 95 },
    { id: 'exercise-1', title: 'Exercise 1', page: 95 },
    { id: 'exercise-2', title: 'Exercise 2', page: 95 },
    { id: 'exercise-3', title: 'Exercise 3', page: 96 },
    { id: 'exercise-4', title: 'Exercise 4', page: 96 },
    { id: 'exercise-5', title: 'Exercise 5', page: 96 },
    { id: 'quiz-game', title: 'Observation Quiz Game', page: 97 },
    { id: 'scenario-practice', title: 'Scenario Generator', page: 97 },
  ];

  const progress_pct = ((currentSection + 1) / sections.length) * 100;

  const togglePresentationMode = async () => {
    if (!isPresentationMode) {
      try {
        if (document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) { console.log('Fullscreen not available'); }
    } else {
      try {
        if (document.fullscreenElement) {
          await document.exitFullscreen();
        }
      } catch (e) { console.log('Exit fullscreen failed'); }
    }
    setIsPresentationMode(!isPresentationMode);
  };

  // Render content based on section
  const renderContent = () => {
    switch (currentSection) {
      case 0: // Intro
        return (
          <div className="space-y-8 animate-fadeIn">
            <div className="relative rounded-xl overflow-hidden shadow-2xl h-64 lg:h-96">
              <img src={CHAPTER_IMAGES.hero} alt="Observation" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent flex items-end p-8">
                <div>
                  <h2 className="text-4xl lg:text-5xl font-bold text-white mb-2">What is an Observation?</h2>
                  <p className="text-xl text-blue-200">Chapter 2: The Art of Seeing Clearly</p>
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-blue-900/20 border border-blue-700/30 rounded-xl p-6">
                <ObserveIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Notice without Evaluating</h3>
                <p className="text-slate-300">
                  An observation is what you notice, <span className="text-blue-300 font-semibold">independent of any evaluation</span>.
                  It is the raw data of your experience, stripped of judgment or interpretation.
                </p>
              </div>
              <div className="bg-emerald-900/20 border border-emerald-700/30 rounded-xl p-6">
                <ObserveIcon size={32} className="mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">The Camera Test</h3>
                <p className="text-slate-300">
                  An observation is <span className="text-emerald-300 font-semibold">what you could videotape or audio record</span>.
                  A camera doesn't judge; it just records action and sound.
                </p>
              </div>
            </div>
          </div>
        );

      case 1: // Why
        return (
          <div className="space-y-8 animate-fadeIn text-center max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">Why say the Observation?</h2>
            <div className="bg-slate-800/50 rounded-2xl p-8 border border-slate-700">
              <p className="text-xl text-slate-200 leading-relaxed mb-8">
                The purpose is to <span className="text-amber-400 font-semibold">invite the listener's attention</span> to the 
                specific event, behavior, or words we are speaking about.
              </p>
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div className="bg-red-900/20 border border-red-700/30 p-6 rounded-xl">
                  <h4 className="text-red-300 font-bold mb-2">Vague Expression</h4>
                  <p className="text-white italic">"I'm upset because I need caring"</p>
                  <p className="text-red-200/60 text-sm mt-2">
                    Does not supply enough information to identify what we are talking about.
                    <br/><strong className="text-red-400">Result: Increased Confusion</strong>
                  </p>
                </div>
                <ArrowRightIcon size={32} color="#64748B" className="hidden md:block mx-auto" />
                <div className="bg-green-900/20 border border-green-700/30 p-6 rounded-xl">
                  <h4 className="text-green-300 font-bold mb-2">Clear Observation</h4>
                  <p className="text-white italic">"When you walked away while I was speaking..."</p>
                  <p className="text-green-200/60 text-sm mt-2">
                    Directs attention to the specific stimulus.
                    <br/><strong className="text-green-400">Result: Shared Reality</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        );

      case 2: // Remember
        return (
          <div className="space-y-8 animate-fadeIn">
            <h2 className="text-3xl font-bold text-white text-center mb-8">Things to Remember</h2>
            <div className="grid gap-6">
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 rounded-xl border-l-4 border-amber-500">
                <h3 className="text-xl font-bold text-amber-400 mb-2">Separate from Judgment</h3>
                <p className="text-slate-300">
                  Clearly separate your observation from any <span className="text-white">judgments, diagnoses, labels, analyses</span>.
                  This increases the likelihood that the listener will hear your message without blame, criticism, or guilt-tripping.
                </p>
              </div>
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 rounded-xl border-l-4 border-blue-500">
                <h3 className="text-xl font-bold text-blue-400 mb-2">Reduce Disputes</h3>
                <p className="text-slate-300">
                  If you are making a <span className="text-white">pure, judgment-free observation</span>, the listener is 
                  less likely to dispute it (except in cases where they remember things differently).
                </p>
              </div>
              <div className="bg-gradient-to-r from-slate-800 to-slate-900 p-6 rounded-xl border-l-4 border-purple-500">
                <h3 className="text-xl font-bold text-purple-400 mb-2">Common Pitfall</h3>
                <p className="text-slate-300">
                  Observations are most often confused with <span className="text-white">evaluations or judgments</span>.
                  "You're lazy" is an evaluation. "You haven't taken out the trash" is an observation.
                </p>
              </div>
            </div>
          </div>
        );

      case 3: // Why Hard
        return (
          <div className="space-y-8 animate-fadeIn">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold text-white mb-6">Why is the Observation so hard?</h2>
                <div className="space-y-4 text-lg text-slate-300">
                  <p>
                    Most of us have been <span className="text-amber-400">conditioned since birth</span> to avoid feeling our feelings 
                    by focusing on external factors, mainly other people and situations.
                  </p>
                  <p>
                    We commonly patterns where:
                  </p>
                  <ul className="list-disc pl-6 space-y-2">
                    <li>Someone does something we don't like</li>
                    <li>We have an <span className="text-red-400">evaluation</span> about it (often unconscious)</li>
                    <li>This leads to feelings we try not to feel</li>
                    <li>We avoid the feeling by <span className="text-red-400">judging</span> the other person</li>
                  </ul>
                </div>
              </div>
              <div className="relative h-full min-h-[300px] rounded-xl overflow-hidden">
                <img src={CHAPTER_IMAGES.confusion} alt="Confusion" className="absolute inset-0 w-full h-full object-cover opacity-70" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-black/60 p-6 rounded-xl backdrop-blur-sm text-center">
                    <p className="text-xl font-serif text-white italic">
                      "We avoid this feeling by judging the other person by labeling them, usually as something that we define as 'bad.'"
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4: // Pattern Example
        return (
          <div className="space-y-6 animate-fadeIn">
            <h2 className="text-2xl font-bold text-white text-center mb-4">The Pattern of Disconnection</h2>
            
            <div className="max-w-3xl mx-auto bg-slate-800/50 p-6 rounded-xl border border-slate-700">
              <h3 className="text-amber-400 font-bold mb-4">An Example Scenario:</h3>
              <p className="text-slate-300 mb-6">
                My partner says, <span className="text-white italic">“You're selfish”</span> while looking at me.
              </p>
              
              <div className="space-y-4 relative pl-8 border-l-2 border-slate-600">
                <div className="relative">
                  <span className="absolute -left-[41px] bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs">1</span>
                  <p className="text-slate-300">I immediately consider whether a part of me believes that I am selfish or not.</p>
                </div>
                <div className="relative">
                  <span className="absolute -left-[41px] bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs">2</span>
                  <p className="text-slate-300">I find a part of me that thinks I am selfish and <span className="text-red-400">I don't like that discovery</span>.</p>
                </div>
                <div className="relative">
                  <span className="absolute -left-[41px] bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs">3</span>
                  <p className="text-slate-300">I have bodily sensations and feelings of sadness. I avoid feeling them.</p>
                </div>
                <div className="relative">
                  <span className="absolute -left-[41px] bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs">4</span>
                  <p className="text-slate-300">
                    I accomplish this by <span className="text-red-400">judging/blaming/criticizing</span> the other person as “bad” or “wrong” 
                    to discredit what I believe to be the source of my pain.
                  </p>
                </div>
                <div className="relative">
                  <span className="absolute -left-[41px] bg-slate-700 rounded-full w-6 h-6 flex items-center justify-center text-xs">5</span>
                  <p className="text-white italic font-medium">“Well, you're no saint.”</p>
                </div>
              </div>
              
              <div className="mt-6 bg-blue-900/20 p-4 rounded-lg">
                <p className="text-blue-200 text-sm text-center">
                  This happens in seconds. We are trained to believe others are the cause of our suffering, 
                  making pure observation very difficult.
                </p>
              </div>
            </div>
          </div>
        );

      case 5: // Exercise Intro
        return (
          <div className="flex flex-col items-center justify-center h-full space-y-8 animate-fadeIn">
            <div className="w-24 h-24 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/20 mb-4">
              <ObserveIcon size={48} />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-white text-center">Practice Time!</h2>
            <p className="text-xl text-slate-300 text-center max-w-2xl">
              Determine whether the following statements are a <span className="text-emerald-400 font-bold">pure observation</span> or 
              contain an <span className="text-red-400 font-bold">evaluation</span>.
            </p>
            <div className="bg-slate-800 p-6 rounded-xl border border-slate-700 max-w-2xl w-full">
              <h3 className="text-lg font-bold text-white mb-2">Instructions:</h3>
              <ul className="list-disc pl-5 space-y-2 text-slate-300">
                <li>If it is an evaluation, rewrite it as an observation.</li>
                <li><strong>Hint:</strong> Think about what a video camera would record.</li>
                <li>Make up an observation that might stimulate these responses.</li>
              </ul>
            </div>
            <Button 
              onClick={() => setCurrentSection(prev => prev + 1)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white text-lg px-8 py-6 rounded-full shadow-lg hover:shadow-xl transition-all"
            >
              Start Exercise <ArrowRightIcon size={24} className="ml-2" />
            </Button>
          </div>
        );

      // Exercises 1-5 logic
      default:
        if (currentSection >= 6 && currentSection <= 10) {
          const exIndex = currentSection - 6;
          const exData = exerciseData[exIndex];
          const userResponse = observationExercises[exData.id] || {};
          const isRevealed = showExerciseAnswers[exData.id];

          return (
            <div className="space-y-8 animate-fadeIn max-w-3xl mx-auto">
              <div className="flex items-center justify-between">
                <span className="bg-slate-700 text-slate-300 px-3 py-1 rounded-full text-sm font-bold">
                  Exercise {exIndex + 1} of 5
                </span>
                <span className="text-slate-500 text-sm">Think like a camera 🎥</span>
              </div>

              <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-serif text-white mb-6 text-center">"{exData.statement}"</h3>
                
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <button
                    onClick={() => {
                      const newData = { ...userResponse, type: 'observation' };
                      setObservationExercises({ ...observationExercises, [exData.id]: newData });
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      userResponse.type === 'observation' 
                        ? 'bg-emerald-600/20 border-emerald-500 text-emerald-300' 
                        : 'bg-slate-700/30 border-slate-600 hover:border-emerald-500/50 text-slate-400'
                    }`}
                  >
                    <CheckIcon size={24} />
                    Pure Observation
                  </button>
                  <button
                    onClick={() => {
                      const newData = { ...userResponse, type: 'evaluation' };
                      setObservationExercises({ ...observationExercises, [exData.id]: newData });
                    }}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      userResponse.type === 'evaluation' 
                        ? 'bg-amber-600/20 border-amber-500 text-amber-300' 
                        : 'bg-slate-700/30 border-slate-600 hover:border-amber-500/50 text-slate-400'
                    }`}
                  >
                    <X className="w-6 h-6 mx-auto mb-2" />
                    Contains Evaluation
                  </button>
                </div>

                {/* Rewrite Section */}
                {userResponse.type === 'evaluation' && (
                  <div className="space-y-3 animate-in slide-in-from-top duration-300">
                    <label className="text-sm font-medium text-slate-300">Rewrite as a pure observation:</label>
                    <Textarea
                      value={userResponse.rewrite || ''}
                      onChange={(e) => {
                        const newData = { ...userResponse, rewrite: e.target.value };
                        setObservationExercises({ ...observationExercises, [exData.id]: newData });
                      }}
                      placeholder="When I see/hear..."
                      className="bg-slate-900/50 border-slate-600 text-white min-h-[100px]"
                    />
                  </div>
                )}

                {/* Feedback Area */}
                {isRevealed && (
                  <div className="mt-6 p-4 rounded-xl bg-blue-900/20 border border-blue-500/30 animate-in fade-in">
                    <h4 className="font-bold text-blue-300 mb-2 flex items-center gap-2">
                      <InsightIcon size={16} /> Insight
                    </h4>
                    <p className="text-slate-300 mb-2">
                      <strong className={exData.isObservation ? "text-emerald-400" : "text-amber-400"}>
                        {exData.isObservation ? "This IS a pure observation." : "This contains evaluation."}
                      </strong>
                    </p>
                    {!exData.isObservation && (
                      <p className="text-slate-400 text-sm">
                        <strong className="text-white">Example rewrite:</strong> "{exData.rewrite}"
                      </p>
                    )}
                    <p className="text-slate-500 text-xs mt-3 italic">Hint: {exData.hint}</p>
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                <Button
                  onClick={() => setShowExerciseAnswers({ ...showExerciseAnswers, [exData.id]: true })}
                  variant="ghost"
                  className="text-slate-400 hover:text-white"
                  disabled={isRevealed}
                >
                  {isRevealed ? 'Answer Revealed' : 'Check Answer'}
                </Button>
              </div>
            </div>
          );
        }

        // Quiz Game - Page 97 Answers
        if (currentSection === 11) {
          const currentQuiz = quizData[currentQuizIndex];
          const totalQuestions = quizData.length;
          const answeredCount = Object.keys(quizAnswered).length;
          
          const handleQuizAnswer = (isCorrectGuess) => {
            const isActuallyCorrect = currentQuiz.isObservation === isCorrectGuess;
            setQuizAnswered(prev => ({ ...prev, [currentQuiz.id]: { guess: isCorrectGuess, correct: isActuallyCorrect } }));
            if (isActuallyCorrect) {
              setQuizScore(prev => prev + 1);
            }
          };

          const goToNextQuestion = () => {
            if (currentQuizIndex < totalQuestions - 1) {
              setCurrentQuizIndex(prev => prev + 1);
            } else {
              setQuizComplete(true);
            }
          };

          const resetQuiz = () => {
            setQuizScore(0);
            setQuizAnswered({});
            setQuizComplete(false);
            setCurrentQuizIndex(0);
          };

          const hasAnswered = quizAnswered[currentQuiz?.id] !== undefined;
          const wasCorrect = quizAnswered[currentQuiz?.id]?.correct;

          return (
            <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
              {/* Header */}
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-amber-500 to-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/20">
                  <ObserveIcon size={40} />
                </div>
                <h2 className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3">
                  <GlyphIcon className="w-8 h-8 text-amber-400" />
                  Observation Quiz Game
                </h2>
                <p className="text-slate-300 text-lg mb-4">
                  Test your "Camera Lens" skills! Based on page 97 answer key.
                </p>
                
                {/* Score Display */}
                <div className="flex items-center justify-center gap-4 mb-6">
                  <div className="bg-emerald-900/30 border border-emerald-500/30 rounded-lg px-4 py-2">
                    <span className="text-emerald-400 font-bold text-lg">{quizScore}</span>
                    <span className="text-slate-400 text-sm ml-1">correct</span>
                  </div>
                  <div className="bg-slate-800/50 border border-slate-600/30 rounded-lg px-4 py-2">
                    <span className="text-white font-bold text-lg">{currentQuizIndex + 1}</span>
                    <span className="text-slate-400 text-sm"> / {totalQuestions}</span>
                  </div>
                </div>
              </div>

              {/* Quiz Complete Screen */}
              {quizComplete ? (
                <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-700 rounded-2xl p-8 text-center">
                  <div className="mb-4 flex justify-center">
                    {quizScore >= 10 ? <CrystalIcon className="w-16 h-16 text-amber-400" /> : 
                     quizScore >= 7 ? <StarburstIcon className="w-16 h-16 text-amber-400" /> : 
                     quizScore >= 4 ? <CheckAlchemyIcon className="w-16 h-16 text-emerald-400" /> : 
                     <SpiralIcon className="w-16 h-16 text-blue-400" />}
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Quiz Complete!</h3>
                  <p className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-orange-500 mb-4">
                    {quizScore} / {totalQuestions}
                  </p>
                  <p className="text-slate-300 mb-6 flex items-center justify-center gap-2">
                    {quizScore >= 10 ? <><span>Outstanding! You're a Camera Lens Master!</span><CameraLensIcon className="w-5 h-5 text-amber-400" /></> : 
                     quizScore >= 7 ? "Great job! Your observation skills are sharp!" : 
                     quizScore >= 4 ? "Good effort! Keep practicing the Camera Test." : 
                     "Keep learning! Review the chapter and try again."}
                  </p>
                  <div className="flex gap-4 justify-center">
                    <Button onClick={resetQuiz} className="bg-amber-600 hover:bg-amber-500 text-white">
                      <RotateCcw className="w-4 h-4 mr-2" /> Play Again
                    </Button>
                    <Button onClick={() => setCurrentSection(12)} variant="outline" className="border-slate-600 text-white hover:bg-slate-800">
                      Try More Scenarios →
                    </Button>
                  </div>
                </div>
              ) : currentQuiz && (
                <div className="space-y-6">
                  {/* Question Card */}
                  <div className="bg-slate-800/80 border border-slate-600 rounded-2xl p-8 shadow-xl">
                    <div className="flex items-center justify-between mb-4">
                      <span className="bg-amber-500/20 text-amber-300 text-sm font-bold px-3 py-1 rounded-full">
                        Question {currentQuizIndex + 1}
                      </span>
                      <span className="text-slate-500 text-sm flex items-center gap-1">Think like a camera <CameraLensIcon className="w-4 h-4" /></span>
                    </div>
                    
                    <h3 className="text-2xl font-serif text-white mb-8 text-center leading-relaxed">
                      "{currentQuiz.statement}"
                    </h3>
                    
                    {/* Answer Buttons */}
                    {!hasAnswered ? (
                      <div className="grid grid-cols-2 gap-4">
                        <button
                          onClick={() => handleQuizAnswer(true)}
                          className="p-6 rounded-xl border-2 border-emerald-500/50 bg-emerald-900/20 hover:bg-emerald-900/40 hover:border-emerald-400 transition-all group"
                        >
                          <CheckIcon size={32} color="#10B981" className="group-hover:scale-110 transition-transform" />
                          <p className="text-emerald-300 font-bold">Pure Observation</p>
                          <p className="text-emerald-200/60 text-sm mt-1">A camera could record this</p>
                        </button>
                        <button
                          onClick={() => handleQuizAnswer(false)}
                          className="p-6 rounded-xl border-2 border-amber-500/50 bg-amber-900/20 hover:bg-amber-900/40 hover:border-amber-400 transition-all group"
                        >
                          <X className="w-8 h-8 mx-auto mb-2 text-amber-400 group-hover:scale-110 transition-transform" />
                          <p className="text-amber-300 font-bold">Contains Evaluation</p>
                          <p className="text-amber-200/60 text-sm mt-1">Includes judgment or interpretation</p>
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-4 animate-in fade-in duration-300">
                        {/* Result Banner */}
                        <div className={`p-4 rounded-xl ${wasCorrect ? 'bg-emerald-900/30 border border-emerald-500/40' : 'bg-red-900/30 border border-red-500/40'}`}>
                          <div className="flex items-center gap-3">
                            {wasCorrect ? (
                              <CheckIcon size={32} color="#10B981" />
                            ) : (
                              <X className="w-8 h-8 text-red-400" />
                            )}
                            <div>
                              <p className={`font-bold text-lg flex items-center gap-2 ${wasCorrect ? 'text-emerald-300' : 'text-red-300'}`}>
                                {wasCorrect ? <><StarburstIcon className="w-5 h-5" /> Correct!</> : 'Not quite...'}
                              </p>
                              <p className="text-slate-300">
                                This {currentQuiz.isObservation ? 'IS' : 'is NOT'} a pure observation.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Explanation */}
                        <div className="bg-blue-900/20 border border-blue-500/30 rounded-xl p-4">
                          <h4 className="text-blue-300 font-bold mb-2 flex items-center gap-2">
                            <InsightIcon size={16} /> Why?
                          </h4>
                          <p className="text-slate-300">{currentQuiz.explanation}</p>
                          {currentQuiz.rewrite && (
                            <div className="mt-3 pt-3 border-t border-blue-500/20">
                              <p className="text-blue-200 text-sm">
                                <strong>Better observation:</strong> "{currentQuiz.rewrite}"
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Next Button */}
                        <div className="flex justify-center pt-4">
                          <Button onClick={goToNextQuestion} className="bg-white text-slate-900 hover:bg-slate-200 px-8">
                            {currentQuizIndex < totalQuestions - 1 ? 'Next Question →' : <span className="flex items-center gap-2">See Results <CrystalIcon className="w-5 h-5" /></span>}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Progress Dots */}
                  <div className="flex justify-center gap-2">
                    {quizData.map((_, idx) => (
                      <div
                        key={idx}
                        className={`w-3 h-3 rounded-full transition-all ${
                          idx === currentQuizIndex ? 'bg-amber-500 scale-125' :
                          quizAnswered[quizData[idx].id]?.correct ? 'bg-emerald-500' :
                          quizAnswered[quizData[idx].id] ? 'bg-red-500' :
                          'bg-slate-700'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        }

        // Scenario Generator
        if (currentSection === 12) {
          return (
            <div className="max-w-4xl mx-auto space-y-8 animate-fadeIn">
              <div className="text-center">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-purple-500/20">
                  <InsightIcon size={40} color="#ffffff" />
                </div>
                <h2 className="text-3xl font-bold text-white mb-4">Scenario Generator</h2>
                <p className="text-slate-300 text-lg mb-8">
                  Want to keep practicing? Generate a unique scenario to test your "Camera Lens".
                </p>
              </div>

              <AIPracticeScenario 
                chapterId={2} 
                chapterTitle="Observation vs Evaluation" 
                context="Generate a statement that mixes observation and evaluation for the student to disentangle." 
              />
            </div>
          );
        }
        return null;
    }
  };

  return (
    <div className={isPresentationMode ? "fixed inset-0 z-50 bg-slate-900 overflow-auto" : "min-h-screen bg-slate-900"}>
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-900/90 backdrop-blur-md border-b border-slate-800">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {!isPresentationMode && (
              <Button variant="ghost" size="sm" onClick={() => navigate('/belonging-ebook')} className="text-slate-400 hover:text-white">
                <ArrowLeftIcon size={16} className="mr-2" /> Back
              </Button>
            )}
            <h1 className="font-bold text-white hidden md:block">Chapter 2: Observation</h1>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-500 mr-2">
              {currentSection + 1} / {sections.length}
            </span>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={saveProgress}
              disabled={isSyncing}
              className="border-slate-700 text-slate-300 hover:text-white"
            >
              {isSyncing ? <Cloud className="w-4 h-4 animate-pulse" /> : <SaveIcon size={16} />}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={togglePresentationMode}
              className="text-purple-400 hover:text-purple-300 hover:bg-purple-900/20"
            >
              {isPresentationMode ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
          </div>
        </div>
        <Progress value={progress_pct} className="h-1 bg-slate-800" indicatorClassName="bg-gradient-to-r from-blue-500 to-emerald-500" />
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8 lg:py-12 min-h-[calc(100vh-140px)]">
        {renderContent()}
      </div>

      {/* Footer Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-slate-900/90 backdrop-blur border-t border-slate-800 p-4 z-40">
        <div className="container mx-auto flex items-center justify-between">
          <Button
            onClick={() => setCurrentSection(prev => Math.max(0, prev - 1))}
            disabled={currentSection === 0}
            variant="ghost"
            className="text-slate-400 hover:text-white"
          >
            <ArrowLeftIcon size={24} className="mr-2" /> Previous
          </Button>

          <div className="hidden md:block text-slate-500 text-sm font-medium">
            {sections[currentSection].title}
          </div>

          <Button
            onClick={() => setCurrentSection(prev => Math.min(sections.length - 1, prev + 1))}
            disabled={currentSection === sections.length - 1}
            className="bg-white text-slate-900 hover:bg-slate-200"
          >
            Next <ArrowRightIcon size={24} className="ml-2" />
          </Button>
        </div>
      </div>
      
      {/* Floating Feelings & Needs Helper */}
      <FloatingFeelingsHelper />
    </div>
  );
}
